import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import Property from "../models/Property";
import PropertiesService from "../services/PropertiesService";
import { QueryKeyFactory } from "../services/queryKeys";
import { unwrap } from "../utils/queryFn";
import { createTableQuery, refreshTableQuery } from "../utils/tableQuery";

export const propertiesKeys = new QueryKeyFactory(PropertiesService.path);

export const usePropertiesList = () => {
  const queryClient = useQueryClient();
  const [forceUpdateOn, setForceUpdateOn] = useState<number>();

  const onDataFetch = useMemo(
    () =>
      createTableQuery<Property>({
        queryKey: (page, pageSize, sortBy, sortDir) => propertiesKeys.list({ page, pageSize, sortBy, sortDir }),
        queryFn: (page, pageSize) => unwrap(PropertiesService.getAll(page, pageSize)),
        client: queryClient,
      }),
    [queryClient],
  );

  const refreshTable = () => refreshTableQuery(propertiesKeys.lists(), () => setForceUpdateOn(Date.now()), queryClient);

  const createMutation = useMutation({
    mutationFn: (property: Property) => unwrap(PropertiesService.create(property)),
    onSuccess: async () => {
      await refreshTable();
      NotificationService.info("Property created successfully");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (property: Property) => unwrap(PropertiesService.updateProperty(property)),
    onSuccess: async () => {
      await refreshTable();
      NotificationService.info("Property updated successfully");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (property: Property) => unwrap(PropertiesService.deleteProperty(property)),
    onSuccess: async () => {
      await refreshTable();
      NotificationService.info("Property deleted successfully");
    },
  });

  return {
    onDataFetch,
    forceUpdateOn,
    handleOnCreate: (property: Property) => createMutation.mutateAsync(property),
    handleOnUpdate: (property: Property) => updateMutation.mutateAsync(property),
    handleOnDelete: (property: Property) => deleteMutation.mutate(property),
  };
};
